import React, { ChangeEventHandler } from 'react';

import { ICompany } from './models/ICompany';
import { InviteType } from '../src/global/types';

export type TValues = {
  [field: string]: string;
};

export type TLoginValues = {
  email: string;
  password: string;
};

export type TSignUpValues = {
  firstName: string;
  lastName: string;
  email: string;
};

export type TResetPasswordValue = {
  email: string;
};

export type TConfirmPasswordValue = {
  password: string;
  confirmPassword: string;
};

export type TPaymentForm = {
  firstName: string;
  lastName: string;
  email: string;
  hasReadTerms: boolean;
};

export type TAgreementValues = {
  hasReadTerms: boolean;
};

export interface IInputCustom {
  handleBlur: ChangeEventHandler<HTMLInputElement>;
}

export type TNavItem = {
  label: string;
  shortLabel?: string;
  path: string;
  items?: TNavItem[];
};

export type TNavTab = {
  header: string;
  description?: string;
  items: TNavItem[];
};

export type TOption = {
  text: string;
  value: any;
};

export type TClassStatus =
  | 'No Attend'
  | 'Booked'
  | 'Transferred'
  | 'Cancelled'
  | 'Completed'
  | 'Attended'
  | null
  | string;

export type TColor =
  | 'base'
  | 'neutral'
  | 'primary'
  | 'danger'
  | 'green'
  | 'yellow'
  | 'red'
  | 'blue';

export type TClass = {
  id: string;
  sfEventId: string;
  name: string;
  type: 'Public Class' | 'Private Class';
  courseType: CourseType;
  isParent: boolean;
  description: string;
  status: TClassStatus;
  startTime: string;
  endTime: string;
  companyName: string;
  attended: boolean;
  evaluationLink: string;
  isEvaluated: boolean;
  joinLink: string;
  address?: string;
  companyPhoto?: string;
  readingMaterialLink?: string;
  credentials?: {
    roomEmail: string;
    roomPassword: string;
  };
};

export type TLogin = {
  accessToken: string;
  email: string;
  resolvedInvite?: TResolvedInvite;
};

export type TCourseFilters = {
  parentCategories: TNamedItem[];
  categories: TNamedItem[];
  competencies: TNamedItem[];
  stacks: TNamedItem[];
};

export type TActiveFilters = {
  primaryFilter: TNamedItem | null;
  secondaryFilter: TNamedItem | null;
};

export type TNamedItem = {
  id: string;
  name: string;
};

export type TReview = {
  quote: string;
} & TNamedItem;

export type TCoursePreview = {
  id: string;
  productId: string;
  name: string;
  slug: string;
  whoShouldAttend: string;
  priceRange?: string;
  nearestStartTime?: string;
  imageUrl?: string;
  categoryIds: string[];
  skillIds: string[];
  primaryCompetencyIds: string[];
  skillStacks?: string[];
};

export type TCourse = {
  prerequisites: string;
  outline: string;
  skills: TNamedItem[];
  reviews: TReview[];
} & TCoursePreview;

export type TPrivateCourse = {
  availableTime: Omit<TAvailableTime, 'price'>;
  coursePreview: TCoursePreview;
};

export type TPrivateSeries = {
  masterOppId: string;
  name: string;
  coursePreview: TCoursePreview;
  subItems: (Omit<TAvailableTime, 'price'> & { imageUrl?: string })[];
};

export type TPrivateCoursesResponse = {
  courses: TPrivateCourse[];
  series: TPrivateSeries[];
};

export type TCourseGroup = {
  groupName: string;
  courses: TCoursePreview[];
};

export type TInviteType =
  | 'company'
  | 'privateClass'
  | 'classSeries'
  | 'program';

export type TResolvedInvite = {
  inviteType: InviteType;
  itemName: string;
  inviteItemCreated: boolean;
  enforceSaml?: boolean;
  userPresent?: boolean;
  errorCode?: ErrorCode;
};

export type TInviteDetails = {
  company: ICompany | null;
};

export type TInviteRequest = {
  inviteType: TInviteType;
  sfProgramId?: string | null;
  sfLearnitCourseId?: string | null;
  sfSeriesId?: string | null;
};

export type TInviteResponse = {
  resolvedInvite: TResolvedInvite;
};

export type TAvailableModule = {
  id: string;
  name?: string;
  availableTimes: TAvailableTime[];
};

export type TAvailableTime = {
  id: string;
  name: string;
  price: number;
  startTime: string;
  endTime: string;
  isWhitelisted?: boolean;
  isBooked?: boolean;
  isFull?: boolean;
  isWaitlisted?: boolean;
  subItems?: TAvailableTimeSubItem[];
  parentAvailableTime?: Omit<TAvailableTime, 'parentAvailableTime'>;
};

export type TAvailableTimeSubItem = {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
};

export enum CourseType {
  fullDay = 'Full Day',
  cohort = 'Cohort',
  series = 'Series',
  module1 = 'Module 1',
  module2 = 'Module 2',
  module3 = 'Module 3',
  module4 = 'Module 4',
  module5 = 'Module 5',
  module6 = 'Module 6',
}

export type TCalendarEvent = {
  availableTime: TAvailableTime | Omit<TAvailableTime, 'price'>;
  courseInfo: {
    slug?: string;
    name: string;
    type: CourseType;
    imageUrl?: string;
    whoShouldAttend?: string;
    isPrivate: boolean;
  };
};

export type TPublicCartItem = {
  included: boolean;
  studentIds?: string[];
  course: Pick<TCoursePreview, 'name' | 'imageUrl'>;
} & TAvailableTime;

export type TPrivateCartItem = Omit<TPublicCartItem, 'price'>;

export type TCartItem = TPublicCartItem | TPrivateCartItem;

export type TCart = {
  public: TPublicCartItem[];
  private: TPrivateCartItem[];
  processed: TCartItem[];
  status: TCheckoutStatus;
  createdAt: number;
  coupon?: TCoupon;
};

export type TCoupon = {
  code: string;
  percentOff?: number;
  amountOff?: number;
};

export type TCouponValidation = {
  amount: number;
  newAmount: number;
  isValid: boolean;
  metadata: {
    couponUsed: boolean;
    couponCode: string;
    reason: string;
    percentOff?: number;
    amountOff?: number;
  };
};

export type TCheckoutPayment = {
  items: TPublicCartItem[];
  checkoutStudents: TStudent[] | null;
  guestDetails?: {
    email: string;
    firstName: string;
    lastName: string;
  };
  couponCode?: string;
};

export type TInitialCheckoutResult = {
  itemsToPay: TPublicCartItem[];
  processedItems: TCartItem[];
};

export type TStudent = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type TCheckoutStatus = 'open' | 'second_step' | 'completed';

export type TAssignmentUpdate = { itemId: string; studentId: string };

export type TBaseProgram = {
  id: string;
  type: ProgramType;
  startDate: string;
  endDate: string;
};

export type TProgramInviteValues = {
  sfProgramId: string;
  expiresAt: string;
  maxRegistrations: string;
};

export type TProgramInvite = {
  _id: string;
  token: string;
  maxRegistrations: string;
  registrationCount: string;
  expiresAt: string;
  company: ICompany;
  program: { id: string; name: string; type: ProgramType };
};

export type TProgram = {
  publicEnrollmentEnabled: boolean;
  privateClassesEnabled: boolean;
  message?: {
    headline: string;
    body: string;
  };
} & TBaseProgram;

export enum ProgramType {
  TEAM_PASS = 'Team Pass',
  TEAM_PASS_PLUS = 'Team Pass Plus',
  PARTNERSHIP = 'Partnership',
  PRIVATE_CLASS_SERIES = 'Private Class Series',
  PRIVATE_SINGLE_CLASS = 'Private Single Class',
  PUBLIC_ENROLLMENT = 'Public Open Enrollment',
  LEARNIT_ANYTIME = 'Learnit Anytime',
}

export type TUploadResponse = {
  url: string;
};

export type ImageLoadEvent = React.SyntheticEvent<HTMLImageElement, Event>;

export type ActionMenuItem = {
  label: string;
  onClick?: () => void;
  hidden?: boolean;
  link?: string;
};

export enum ErrorCode {
  EMAIL_TAKEN = 'email_taken',
  LINK_INVALID = 'link_invalid',
  PROGRAM_FULL = 'program_full',
  CLASS_FULL = 'class_full',
  CLASS_ENDED = 'class_ended',
  SERIES_FULL = 'series_full',
  SERIES_ENDED = 'series_ended',
  NAME_REQUIRED = 'name_required',
  NOT_FOUND = 'not_found',
  PERMISSION_DENIED = 'permission_denied',
  UNEXPECTED_ERROR = 'unexpected_error',
  EMAIL_REQUIRED = 'email_required',
}

export type CalendarEventBasicProps = {
  startTimeString: string;
  endTimeString: string;
  type: CourseType;
  isBooked?: boolean;
};

export type CalendarEventLibraryProps = {
  cartItem: TPublicCartItem | TPrivateCartItem;
  slug?: string;
  isPrivate: boolean;
  isClassInCart: boolean;
} & CalendarEventBasicProps;
